import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  userAddModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import UserAddModal from '@/modal/UserAddModal';
import { hangjungdong } from '@/data/hangjungdong';
import { SmsReceptionApi } from '@/sdk/SmsReceptionApi';
import {
  UserControllerGetUsersParams,
  ChangeUserStateDtoTx,
  SmsReceptions,
} from '@/sdk/data-contracts';
import { smsListRecoil } from '@/atom/SmsAtom';

const SmsList = () => {
  const smsReceptionApi = new SmsReceptionApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [smsListState, setSmsListState] = useRecoilState(smsListRecoil);

  const [searchCondition, setSearchCondition] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: SmsReceptions[] | undefined;

    const dto: UserControllerGetUsersParams = {
      sortBy,
      order,
    };

    try {
      const { data } =
        await smsReceptionApi.smsReceptionControllerGetSmsReceptions(dto, {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        });
      if (data.results) allList = data.results;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '휴대폰 번호',
      '디바이스 모델',
      '디바이스 No',
      'DevEUI',
      '디바이스명',
      '시설물명',
      '사이트',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.phone,
      list.deviceModel,
      list.deviceNumber,
      list.devEui,
      list.deviceName,
      list.facilityName,
      list.site,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterSmsList = async () => {
    const dto: UserControllerGetUsersParams = {
      ...(searchCondition === '휴대폰 번호' && { phone: searchKeyword }),
      ...(searchCondition === '디바이스 모델' && {
        deviceModel: searchKeyword,
      }),
      ...(searchCondition === '디바이스명' && { deviceName: searchKeyword }),
      ...(searchCondition === '시설물명' && { facilityName: searchKeyword }),
      ...(searchCondition === '사이트' && { site: searchKeyword }),
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } =
        await smsReceptionApi.smsReceptionControllerGetSmsReceptions(dto, {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        });
      if (data.results) setSmsListState(data.results);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterSmsList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      smsListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: ChangeUserStateDtoTx = {
        ids: checkItems,
      };
      await smsReceptionApi.smsReceptionControllerDeleteSmsReception(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterSmsList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="SMS 수신 목록" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <IS.SelectType1>
                  <select
                    value={searchCondition}
                    onChange={(e) => {
                      setSearchCondition(e.target.value);
                      setSearchKeyword('');
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">검색 조건</option>
                    <option value="휴대폰 번호">휴대폰 번호</option>
                    <option value="디바이스 모델">디바이스 모델</option>
                    <option value="디바이스명">디바이스명</option>
                    <option value="시설물명">시설물명</option>
                    <option value="사이트">사이트</option>
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  disabled={searchCondition === '' ? true : false}
                  placeholder="검색어 입력"
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5 type="button" onClick={() => downloadCSV('sms_list')}>
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === smsListState?.length &&
                        smsListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>휴대폰 번호</th>
                <th>디바이스 모델</th>
                <th>디바이스 No</th>
                <th>DevEUI</th>
                <th>디바이스명</th>
                <th>시설물명</th>
                <th>사이트</th>
              </tr>
            </thead>
            <tbody>
              {smsListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.phone}</td>
                  <td>{list.deviceModel}</td>
                  <td>{list.deviceNumber}</td>
                  <td>{list.devEui}</td>
                  <td>{list.deviceName}</td>
                  <td>{list.facilityName}</td>
                  <td>{list.site}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default SmsList;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const CheckWrap = styled(CS.Gap24Box)`
  margin-left: 16px;
`;
